import polygonClipping from 'polygon-clipping'
import type { FloorObject } from '../dto/floor-object.dto.js'
import type { FurnitureArea } from '../dto/furniture-area.dto.js'
import type { Primitive } from '../dto/primitive.dto.js'

/*
 * Whether furniture stands inside a room's furniture areas. See
 * `docs/furniture-areas.md`.
 *
 * The floor is the union of the areas, so two that touch or overlap are one
 * floor and a piece may straddle the line between them. A piece is inside when
 * its whole outline lies on the floor, less an inch all round: an inch of slack,
 * so a piece an import drew touching a wall is not flagged.
 *
 * One rule for the editor, the confirm check and anything else that asks, which
 * is why it lives here.
 */

/** A point, as the polygon library takes one. */
type Pair = [number, number]

/** How far past an area's edge a piece may reach, in design pixels: an inch. */
const FLOOR_SLACK = 1

/**
 * What may be left of a piece outside the floor and still count as inside, in
 * square inches: the floating-point dust two areas sharing an edge can leave.
 */
const DUST = 0.5

/** How far a drawn curve may stray from the true one, in design pixels. */
const CURVE_TOLERANCE = 0.1

/** Below this, two numbers are the same: a millionth of an inch. */
const EPS = 1e-6

/** A room's floor, ready to test pieces against. Null from `floorOf` when it has no areas. */
export interface Floor {
  /** Each area's outline. */
  readonly rings: readonly Pair[][]
  readonly minX: number
  readonly minY: number
  readonly maxX: number
  readonly maxY: number
}

/** `[x0, y0, x1, y1, …]` as points, without a repeated closing point. */
export function outlinePoints(points: readonly number[]): Pair[] {
  return ringOf(points)
}

/** The box around an outline `[x0, y0, x1, y1, …]`, or null when it has no points. */
export function outlineBounds(points: readonly number[]): { minX: number; minY: number; maxX: number; maxY: number } | null {
  const ring = ringOf(points)
  return ring.length === 0 ? null : boundsOf(ring)
}

function ringOf(points: readonly number[]): Pair[] {
  const ring: Pair[] = []
  for (let i = 0; i + 1 < points.length; i += 2) {
    const x = points[i]
    const y = points[i + 1]
    if (x !== undefined && y !== undefined && Number.isFinite(x) && Number.isFinite(y)) ring.push([x, y])
  }
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (ring.length > 1 && first && last && first[0] === last[0] && first[1] === last[1]) ring.pop()
  return ring
}

/**
 * The floor these areas make, or null when there are none — and a room with no
 * areas has no rule, so null is "anything goes", not "nothing fits".
 */
export function floorOf(areas: readonly Pick<FurnitureArea, 'points'>[]): Floor | null {
  const rings = areas.map((area) => ringOf(area.points)).filter((ring) => ring.length >= 3)
  if (rings.length === 0) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const ring of rings) {
    for (const [x, y] of ring) {
      minX = Math.min(minX, x)
      minY = Math.min(minY, y)
      maxX = Math.max(maxX, x)
      maxY = Math.max(maxY, y)
    }
  }
  return { rings, minX, minY, maxX, maxY }
}

/**
 * The floor's outline for drawing: the areas merged, so a line two touching
 * areas share is not drawn as a wall between them. Each area's own outline if
 * the merge fails.
 */
export function floorOutline(floor: Floor): Pair[][] {
  try {
    return polygonClipping.union([floor.rings[0] ?? []], ...floor.rings.slice(1).map((ring) => [ring])).flat()
  } catch {
    return floor.rings.map((ring) => [...ring])
  }
}

// ---- a piece's outline -------------------------------------------------------

function rotated(x: number, y: number, degrees: number): Pair {
  if (degrees === 0) return [x, y]
  const r = (degrees * Math.PI) / 180
  const cos = Math.cos(r)
  const sin = Math.sin(r)
  return [x * cos - y * sin, x * sin + y * cos]
}

/** How many points draw a full circle of this radius within the tolerance. */
function stepsFor(radius: number): number {
  if (radius <= CURVE_TOLERANCE) return 8
  const n = Math.ceil(Math.PI / Math.acos(1 - CURVE_TOLERANCE / radius))
  return Math.min(96, Math.max(8, n))
}

/** Points along an ellipse from `from` to `to` degrees (clockwise on screen, as Konva draws). */
function arcPoints(rx: number, ry: number, from: number, to: number): Pair[] {
  const sweep = to - from
  const n = Math.max(1, Math.ceil((stepsFor(Math.max(rx, ry)) * Math.abs(sweep)) / 360))
  const out: Pair[] = []
  for (let i = 0; i <= n; i++) {
    const a = ((from + (sweep * i) / n) * Math.PI) / 180
    out.push([rx * Math.cos(a), ry * Math.sin(a)])
  }
  return out
}

/**
 * Points whose hull covers a primitive, in its piece's own space. Each shape is
 * drawn about its center (`x`, `y`) and turned about it, as the canvas draws it.
 * Writing on a piece is not part of what stands on the floor.
 */
function primitivePoints(p: Primitive): Pair[] {
  let local: Pair[]
  switch (p.shape) {
    case 'rect': {
      const w = p.width / 2
      const h = p.height / 2
      local = [
        [-w, -h],
        [w, -h],
        [w, h],
        [-w, h],
      ]
      break
    }
    case 'circle':
      local = arcPoints(p.radius, p.radius, 0, 360)
      break
    case 'ellipse':
      local = arcPoints(p.radiusX, p.radiusY, 0, 360)
      break
    case 'wedge':
      local = [[0, 0], ...arcPoints(p.radius, p.radius, 0, p.angle)]
      break
    case 'arc': {
      const inner = arcPoints(p.innerRadius, p.innerRadius, 0, p.angle)
      local = [...arcPoints(p.outerRadius, p.outerRadius, 0, p.angle), ...inner]
      break
    }
    case 'line':
      local = ringOf(p.points)
      break
    case 'text':
      return []
  }
  return local.map(([x, y]) => {
    const [rx, ry] = rotated(x, y, p.rotation)
    return [p.x + rx, p.y + ry]
  })
}

function cross(o: Pair, a: Pair, b: Pair): number {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
}

/** The convex hull, counterclockwise by the shoelace sign (Andrew's monotone chain). */
function convexHull(points: readonly Pair[]): Pair[] {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1])
  if (sorted.length < 3) return sorted
  const lower: Pair[] = []
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2] ?? p, lower[lower.length - 1] ?? p, p) <= EPS) lower.pop()
    lower.push(p)
  }
  const upper: Pair[] = []
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i] ?? [0, 0]
    while (upper.length >= 2 && cross(upper[upper.length - 2] ?? p, upper[upper.length - 1] ?? p, p) <= EPS) upper.pop()
    upper.push(p)
  }
  lower.pop()
  upper.pop()
  return [...lower, ...upper]
}

/**
 * A piece's outline on the plan: the hull of everything it draws, placed and
 * turned as it stands. Empty for anything that is not furniture, or that draws
 * nothing.
 */
export function pieceOutline(o: FloorObject): Pair[] {
  if (o.type !== 'furniture') return []
  const points = o.primitives.flatMap(primitivePoints).map(([x, y]): Pair => {
    const [rx, ry] = rotated(x, y, o.rotation)
    return [o.x + rx, o.y + ry]
  })
  return convexHull(points)
}

/**
 * The outline of several pieces taken together — a table with its chairs — as
 * one hull, so a copy of them can be tested at a spot in one go.
 */
export function unitOutline(pieces: readonly FloorObject[]): Pair[] {
  return convexHull(pieces.flatMap(pieceOutline))
}

// ---- the test ----------------------------------------------------------------

/** Keep the part of a convex polygon at least `d` inside the line `a`→`b` (Sutherland–Hodgman). */
function clipInside(poly: readonly Pair[], a: Pair, b: Pair, d: number): Pair[] {
  const ex = b[0] - a[0]
  const ey = b[1] - a[1]
  const len = Math.hypot(ex, ey)
  if (len < EPS) return [...poly]
  const depth = (p: Pair): number => (ex * (p[1] - a[1]) - ey * (p[0] - a[0])) / len - d
  const out: Pair[] = []
  for (let i = 0; i < poly.length; i++) {
    const cur = poly[i] ?? [0, 0]
    const prev = poly[(i + poly.length - 1) % poly.length] ?? cur
    const dc = depth(cur)
    const dp = depth(prev)
    if (dc >= 0 !== dp >= 0) {
      const t = dp / (dp - dc)
      out.push([prev[0] + t * (cur[0] - prev[0]), prev[1] + t * (cur[1] - prev[1])])
    }
    if (dc >= 0) out.push(cur)
  }
  return out
}

function ringArea(ring: readonly Pair[]): number {
  let sum = 0
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i] ?? [0, 0]
    const b = ring[(i + 1) % ring.length] ?? a
    sum += a[0] * b[1] - b[0] * a[1]
  }
  return sum / 2
}

/**
 * A convex outline less `d` all round, or its middle when it is too small to
 * have any left. The hull runs counterclockwise by the shoelace sign, so its
 * inside is to the left of every edge.
 */
function shrink(hull: readonly Pair[], d: number): Pair[] {
  let core: Pair[] = [...hull]
  for (let i = 0; i < hull.length && core.length > 0; i++) {
    core = clipInside(core, hull[i] ?? [0, 0], hull[(i + 1) % hull.length] ?? [0, 0], d)
  }
  if (core.length >= 3 && ringArea(core) > EPS) return core
  const n = hull.length
  return [[hull.reduce((s, p) => s + p[0], 0) / n, hull.reduce((s, p) => s + p[1], 0) / n]]
}

/** Whether a point is inside a ring (even-odd). */
function insideRing(p: Pair, ring: readonly Pair[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i] ?? [0, 0]
    const b = ring[j] ?? [0, 0]
    if (a[1] > p[1] !== b[1] > p[1] && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) {
      inside = !inside
    }
  }
  return inside
}

/** The point of segment `a`–`b` nearest `p`. */
export function closestOnSegment(p: Pair, a: Pair, b: Pair): Pair {
  const ex = b[0] - a[0]
  const ey = b[1] - a[1]
  const len2 = ex * ex + ey * ey
  const t = len2 < EPS ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * ex + (p[1] - a[1]) * ey) / len2))
  return [a[0] + t * ex, a[1] + t * ey]
}

function distanceToSegment(p: Pair, a: Pair, b: Pair): number {
  const q = closestOnSegment(p, a, b)
  return Math.hypot(p[0] - q[0], p[1] - q[1])
}

function distanceToRing(p: Pair, ring: readonly Pair[]): number {
  let best = Infinity
  for (let i = 0; i < ring.length; i++) {
    best = Math.min(best, distanceToSegment(p, ring[i] ?? [0, 0], ring[(i + 1) % ring.length] ?? [0, 0]))
  }
  return best
}

/** Which side of the line `a`→`b` the point `c` is on: -1, 0 (on the line) or 1. */
function side(a: Pair, b: Pair, c: Pair): number {
  const v = cross(a, b, c)
  return Math.abs(v) <= EPS ? 0 : Math.sign(v)
}

/** Whether `p`, known to be on the line through `a` and `b`, is within the segment. */
function withinSegment(p: Pair, a: Pair, b: Pair): boolean {
  return (
    p[0] >= Math.min(a[0], b[0]) - EPS &&
    p[0] <= Math.max(a[0], b[0]) + EPS &&
    p[1] >= Math.min(a[1], b[1]) - EPS &&
    p[1] <= Math.max(a[1], b[1]) + EPS
  )
}

/**
 * Whether segments `a`–`b` and `c`–`d` meet anywhere, ends included. Two on the
 * same line meet only where they overlap or touch: the two sides of a U-shaped
 * room run along one line without meeting.
 */
function segmentsMeet(a: Pair, b: Pair, c: Pair, d: Pair): boolean {
  const s1 = side(a, b, c)
  const s2 = side(a, b, d)
  const s3 = side(c, d, a)
  const s4 = side(c, d, b)
  if (s1 !== s2 && s3 !== s4) return true
  return (
    (s1 === 0 && withinSegment(c, a, b)) ||
    (s2 === 0 && withinSegment(d, a, b)) ||
    (s3 === 0 && withinSegment(a, c, d)) ||
    (s4 === 0 && withinSegment(b, c, d))
  )
}

function boundsOf(points: readonly Pair[]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const [x, y] of points) {
    minX = Math.min(minX, x)
    minY = Math.min(minY, y)
    maxX = Math.max(maxX, x)
    maxY = Math.max(maxY, y)
  }
  return { minX, minY, maxX, maxY }
}

/**
 * The quick answer: wholly inside one area, clear of its edges. A shape whose
 * corners are all inside a simple outline and whose edges never meet it is
 * inside it. Anything near an edge is left to the exact test.
 */
function clearlyInside(shape: readonly Pair[], ring: readonly Pair[]): boolean {
  const s = boundsOf(shape)
  const r = boundsOf(ring)
  if (s.minX < r.minX || s.minY < r.minY || s.maxX > r.maxX || s.maxY > r.maxY) return false
  if (!shape.every((p) => insideRing(p, ring))) return false
  for (let i = 0; i < shape.length; i++) {
    const a = shape[i] ?? [0, 0]
    const b = shape[(i + 1) % shape.length] ?? a
    for (let j = 0; j < ring.length; j++) {
      if (segmentsMeet(a, b, ring[j] ?? [0, 0], ring[(j + 1) % ring.length] ?? [0, 0])) return false
    }
  }
  return true
}

/**
 * Whether an outline lies on the floor, with the inch of slack. The outline is
 * taken as convex — a piece's hull, or a box.
 */
export function outlineOnFloor(outline: readonly Pair[], floor: Floor): boolean {
  if (outline.length === 0) return true
  // A point or a stroke: each of its points within the slack of the floor.
  if (outline.length < 3) {
    return outline.every(
      (p) =>
        floor.rings.some((ring) => insideRing(p, ring)) ||
        floor.rings.some((ring) => distanceToRing(p, ring) <= FLOOR_SLACK),
    )
  }
  // Quick no: shrinking a convex outline moves each side in by the slack, so
  // one whose box reaches past the floor's by more than that is outside.
  const ob = boundsOf(outline)
  if (
    ob.minX + FLOOR_SLACK < floor.minX - EPS ||
    ob.minY + FLOOR_SLACK < floor.minY - EPS ||
    ob.maxX - FLOOR_SLACK > floor.maxX + EPS ||
    ob.maxY - FLOOR_SLACK > floor.maxY + EPS
  ) {
    return false
  }
  const core = shrink(outline, FLOOR_SLACK)
  if (core.length < 3) return outlineOnFloor(core, floor)
  const b = boundsOf(core)
  if (b.minX < floor.minX - EPS || b.minY < floor.minY - EPS || b.maxX > floor.maxX + EPS || b.maxY > floor.maxY + EPS) {
    return false
  }
  if (floor.rings.some((ring) => clearlyInside(core, ring))) return true
  try {
    const outside = polygonClipping.difference([core], ...floor.rings.map((ring) => [ring]))
    let area = 0
    for (const polygon of outside) {
      polygon.forEach((ring, i) => {
        area += i === 0 ? Math.abs(ringArea(ring)) : -Math.abs(ringArea(ring))
      })
    }
    return area <= DUST
  } catch {
    // The geometry could not be worked out. Say inside rather than hold a piece,
    // or a plan, hostage to a rounding failure.
    return true
  }
}

/**
 * Whether a closed outline crosses itself, `[x0, y0, x1, y1, …]`. An area that
 * does has no clear inside, so drawing one is refused when it closes.
 */
export function outlineCrossesItself(points: readonly number[]): boolean {
  const ring = ringOf(points)
  const n = ring.length
  for (let i = 0; i < n; i++) {
    const a = ring[i] ?? [0, 0]
    const b = ring[(i + 1) % n] ?? a
    // Neighbors share a corner, so only edges two or more apart are compared.
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue
      const c = ring[j] ?? [0, 0]
      const d = ring[(j + 1) % n] ?? c
      if (segmentsMeet(a, b, c, d)) return true
    }
  }
  return false
}

/**
 * The area a point is in — the smallest, where areas overlap, since that is the
 * one somebody pointing at it most likely means — or null when it is in none.
 */
export function areaAt<A extends Pick<FurnitureArea, 'points'>>(areas: readonly A[], x: number, y: number): A | null {
  let best: A | null = null
  let bestSize = Infinity
  for (const area of areas) {
    const ring = ringOf(area.points)
    if (ring.length < 3 || !insideRing([x, y], ring)) continue
    const size = Math.abs(ringArea(ring))
    if (size < bestSize) {
      best = area
      bestSize = size
    }
  }
  return best
}

/**
 * Whether two outlines overlap by more than floating-point dust. Touching is not
 * overlapping: chairs drawn edge to edge around a table share their edges.
 */
export function outlinesOverlap(a: readonly Pair[], b: readonly Pair[]): boolean {
  if (a.length < 3 || b.length < 3) return false
  const ba = boundsOf(a)
  const bb = boundsOf(b)
  if (ba.maxX <= bb.minX || bb.maxX <= ba.minX || ba.maxY <= bb.minY || bb.maxY <= ba.minY) return false
  try {
    let area = 0
    for (const polygon of polygonClipping.intersection([[...a]], [[...b]])) {
      polygon.forEach((ring, i) => {
        area += i === 0 ? Math.abs(ringArea(ring)) : -Math.abs(ringArea(ring))
      })
    }
    return area > DUST
  } catch {
    return false
  }
}

/** Whether a piece stands on the floor. Anything that is not furniture always does. */
export function isOnFloor(o: FloorObject, floor: Floor): boolean {
  return outlineOnFloor(pieceOutline(o), floor)
}

/** The furniture standing outside the room's areas. None when the room has no areas. */
export function piecesOffFloor(objects: readonly FloorObject[], areas: readonly Pick<FurnitureArea, 'points'>[]): FloorObject[] {
  const floor = floorOf(areas)
  if (!floor) return []
  return objects.filter((o) => o.type === 'furniture' && !isOnFloor(o, floor))
}
